
import React from 'react';
import { Film, ArrowRight, Mail, MessageSquare, Users, Phone } from 'lucide-react';
import { LocalizedContent } from '../utils/localization';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

interface HeroProps {
  t: any; // translations
  content: LocalizedContent;
}

const Hero: React.FC<HeroProps> = ({ t, content }) => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  
  // Define the contact button path based on language
  const handleContactClick = () => {
    const contactPath = `/${currentLanguage}/${
      currentLanguage === 'de' ? 'kontakt' : 
      currentLanguage === 'en' ? 'contact' : 
      currentLanguage === 'fr' ? 'contact' : 
      'contatto'
    }`;
    navigate(contactPath);
  };

  return (
    <div className="py-24 md:py-28 bg-gradient-to-b from-[#0B0B0B] to-[#101010]">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 pt-10">
          <div className="inline-block mb-6 p-3 rounded-full bg-purple-900/30 backdrop-blur-sm">
            <Film size={40} className="text-purple-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-future text-white micro-glitch">{t.productVideos}</h1>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            {t.productVideosDesc}
          </p>
          <div className="w-3 h-3 rounded-full bg-purple-500 mx-auto mt-6 animate-pulse"></div>
          
          {/* Icon-based contact section */}
          <div className="mt-16 bg-black/40 backdrop-blur-md p-6 rounded-xl border border-purple-500/20 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">{t.contactTitle || content.heroSubtitle}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
              <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
                <div className="bg-purple-600/30 p-3 rounded-full">
                  <MessageSquare className="w-6 h-6 text-purple-300" />
                </div>
                <h3 className="font-semibold text-white text-lg">{t.personalConsultation || "Persönliche Beratung"}</h3>
                <p className="text-gray-300 text-sm">{t.personalConsultationDesc || "Individuelles Strategiegespräch für Ihre Produktvideo Kampagne"}</p>
              </div>
              
              <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
                <div className="bg-purple-600/30 p-3 rounded-full">
                  <Users className="w-6 h-6 text-purple-300" />
                </div>
                <h3 className="font-semibold text-white text-lg">{t.targetAudience}</h3>
                <p className="text-gray-300 text-sm">{t.targetAudienceDesc}</p>
              </div>
              
              <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
                <div className="bg-purple-600/30 p-3 rounded-full">
                  <Phone className="w-6 h-6 text-purple-300" />
                </div>
                <h3 className="font-semibold text-white text-lg">{t.support24h || "24h Support"}</h3>
                <p className="text-gray-300 text-sm">{t.support24hDesc || "Schnelle Antwort innerhalb eines Werktages für Ihre Video-Anfrage"}</p>
              </div>
              
              <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
                <div className="bg-purple-600/30 p-3 rounded-full">
                  <Mail className="w-6 h-6 text-purple-300" />
                </div>
                <h3 className="font-semibold text-white text-lg">{t.freeAnalysis || "Kostenlose Analyse"}</h3>
                <p className="text-gray-300 text-sm">{t.freeAnalysisDesc || "Gratis Video-Marketing-Analyse für Ihren Brand"}</p>
              </div>
            </div>
            
            <div className="mt-8">
              <Button
                onClick={handleContactClick}
                className="bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white px-8 py-5 rounded-lg shadow-lg group"
              >
                {t.contactUs}
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
